import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { categoryService } from '../firebase/services/categoryService';
import type { Category } from '../firebase/schema';

export const useCategoryStore = defineStore('category', () => {
  // State
  const categories = ref<Category[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const currentCategory = ref<Category | null>(null);

  // Getters
  const getCategoryById = (id: string) => {
    return categories.value.find(cat => cat.id === id);
  };

  const getCategoryByName = (name: string) => {
    return categories.value.find(cat => cat.name.toLowerCase() === name.toLowerCase());
  };

  const sortedCategories = computed(() => {
    return [...categories.value].sort((a, b) => a.name.localeCompare(b.name));
  });

  // Actions
  const fetchCategories = async () => {
    try {
      loading.value = true;
      error.value = null;
      categories.value = await categoryService.getAllCategories();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load categories';
      console.error('Error fetching categories:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchCategory = async (id: string) => {
    try {
      loading.value = true;
      error.value = null;
      const category = await categoryService.getCategory(id);
      currentCategory.value = category;
      return category;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch category';
      console.error('Error fetching category:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createCategory = async (categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'wallpaperCount'>) => {
    try {
      loading.value = true;
      error.value = null;
      const newCategory = await categoryService.createCategory(categoryData.name, categoryData.description);
      await fetchCategories(); // Refresh the list
      return newCategory;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create category';
      console.error('Error creating category:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      loading.value = true;
      error.value = null;
      await categoryService.updateCategory(id, updates);
      await fetchCategories(); // Refresh the list
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update category';
      console.error('Error updating category:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      loading.value = true;
      error.value = null;
      await categoryService.deleteCategory(id);
      await fetchCategories(); // Refresh the list
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete category';
      console.error('Error deleting category:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    categories: computed(() => categories.value), // Wrap in computed for better reactivity
    currentCategory,
    loading,
    error,
    
    // Getters
    getCategoryById,
    getCategoryByName,
    sortedCategories,
    
    // Actions
    fetchCategories,
    fetchCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    
    // Internal state (for debugging)
    _categories: categories
  };
});
